import { useRef, useState, useEffect } from 'react';
import { nycCollaborations } from '../../../mocks/nycCollaborations';

export default function NYCCollaborations() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const firstCard = el.querySelector('[data-collab-card]') as HTMLElement | null;
    if (!firstCard) return;
    const gap = 20;
    const scrollAmount = firstCard.offsetWidth + gap;
    el.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
  };

  return (
    <section id="nyc-collabs" className="py-16 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex items-end justify-between mb-8 sm:mb-12">
          <div>
            <span className="inline-block text-xs font-display font-semibold uppercase tracking-[3px] text-sage-500 mb-2 sm:mb-3">
              Portfolio
            </span>
            <h3 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-gray-900 mb-2 sm:mb-4 tracking-tight">
              Brand Collaborations
            </h3>
            <p className="text-xs sm:text-sm text-gray-600 max-w-2xl">
              Partnering with leading brands to create authentic content
            </p>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              className={`w-12 h-12 flex items-center justify-center rounded-full border-2 transition-all cursor-pointer whitespace-nowrap ${
                canScrollLeft
                  ? 'border-sage-400 text-sage-500 hover:bg-sage-500 hover:text-white'
                  : 'border-gray-200 text-gray-300 cursor-not-allowed'
              }`}
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
            <button
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              className={`w-12 h-12 flex items-center justify-center rounded-full border-2 transition-all cursor-pointer whitespace-nowrap ${
                canScrollRight
                  ? 'border-sage-400 text-sage-500 hover:bg-sage-500 hover:text-white'
                  : 'border-gray-200 text-gray-300 cursor-not-allowed'
              }`}
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
          </div>
        </div>
      </div>

      <div className="relative">
        {canScrollLeft && (
          <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none"></div>
        )}
        {canScrollRight && (
          <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none"></div>
        )}

        <div
          ref={scrollRef}
          className="flex gap-4 sm:gap-5 overflow-x-auto px-4 sm:px-6 lg:px-12 pb-4 snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
        >
          {nycCollaborations.map((collab) => (
            <div
              key={collab.id}
              data-collab-card
              className="flex-shrink-0 w-[240px] sm:w-[280px] group cursor-pointer snap-center"
            >
              <div className="relative w-[240px] sm:w-[280px] h-[427px] sm:h-[498px] rounded-2xl overflow-hidden shadow-lg">
                <img
                  src={collab.thumbnail}
                  alt={collab.brand}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>

                <div className="absolute top-3 sm:top-4 left-3 sm:left-4 flex items-center gap-2">
                  <div className="w-7 sm:w-8 h-7 sm:h-8 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full">
                    <i className="ri-play-fill text-white text-xs sm:text-sm"></i>
                  </div>
                  <span className="text-[10px] sm:text-xs font-display font-medium text-white/90 bg-white/20 backdrop-blur-sm px-2.5 sm:px-3 py-1 rounded-full">
                    UGC Video
                  </span>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
                  <h4 className="text-xl sm:text-2xl font-display font-bold text-white">
                    {collab.brand}
                  </h4>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex md:hidden items-center justify-center gap-3 mt-6 px-4 sm:px-6">
        <button
          onClick={() => scroll('left')}
          disabled={!canScrollLeft}
          className={`w-10 sm:w-11 h-10 sm:h-11 flex items-center justify-center rounded-full border-2 transition-all cursor-pointer whitespace-nowrap ${
            canScrollLeft
              ? 'border-sage-400 text-sage-500'
              : 'border-gray-200 text-gray-300 cursor-not-allowed'
          }`}
        >
          <i className="ri-arrow-left-s-line text-base sm:text-lg"></i>
        </button>
        <span className="text-xs sm:text-sm text-gray-500 font-sans">Swipe to explore</span>
        <button
          onClick={() => scroll('right')}
          disabled={!canScrollRight}
          className={`w-10 sm:w-11 h-10 sm:h-11 flex items-center justify-center rounded-full border-2 transition-all cursor-pointer whitespace-nowrap ${
            canScrollRight
              ? 'border-sage-400 text-sage-500'
              : 'border-gray-200 text-gray-300 cursor-not-allowed'
          }`}
        >
          <i className="ri-arrow-right-s-line text-base sm:text-lg"></i>
        </button>
      </div>
    </section>
  );
}
